import { v4 as uuidv4 } from 'uuid';

// 로컬 스토리지 키 상수
const ANONYMOUS_WORKSHEETS_KEY = 'whatquiz_anonymous_worksheets';
const ANONYMOUS_WORKSHEET_PREFIX = 'whatquiz_anonymous_worksheet_';
const ANONYMOUS_FAVORITES_KEY = 'whatquiz_anonymous_favorites';

/**
 * 모든 익명 학습지 목록 가져오기
 * @returns {Array} 익명 사용자의 학습지 목록
 */
export const getAnonymousWorksheets = () => {
  const storedWorksheets = localStorage.getItem(ANONYMOUS_WORKSHEETS_KEY);
  if (storedWorksheets) {
    try {
      return JSON.parse(storedWorksheets);
    } catch (err) {
      console.error('익명 학습지 데이터 파싱 오류:', err);
      return [];
    }
  }
  return [];
};

/**
 * 개별 익명 학습지 데이터 가져오기
 * @param {string} worksheetId 학습지 ID
 * @returns {Object|null} 학습지 데이터 또는 null
 */
export const getAnonymousWorksheet = (worksheetId) => {
  const storedWorksheet = localStorage.getItem(`${ANONYMOUS_WORKSHEET_PREFIX}${worksheetId}`);
  if (storedWorksheet) {
    try {
      return JSON.parse(storedWorksheet);
    } catch (err) {
      console.error('익명 학습지 데이터 파싱 오류:', err);
      return null;
    }
  }
  return null;
};

/**
 * 익명 학습지 저장
 * @param {Object} worksheetData 학습지 데이터
 * @returns {Object} 저장 결과
 */
export const saveAnonymousWorksheet = (worksheetData) => {
  try {
    // 기존 학습지 목록 가져오기
    const worksheets = getAnonymousWorksheets();
    
    // 새 학습지 ID 생성
    const worksheetId = uuidv4();
    
    // 타임스탬프 생성
    const timestamp = new Date().toISOString();
    
    // 새 학습지 데이터
    const newWorksheet = {
      id: worksheetId,
      ...worksheetData,
      isPublic: true, // 모든 학습지 공개 상태로 설정
      createdAt: timestamp,
      updatedAt: timestamp,
      favorite: false
    };
    
    // 개별 학습지 데이터 저장
    localStorage.setItem(
      `${ANONYMOUS_WORKSHEET_PREFIX}${worksheetId}`,
      JSON.stringify(newWorksheet)
    );
    
    // 학습지 목록에 추가
    worksheets.push({
      id: worksheetId,
      title: worksheetData.title || worksheetData.topic || '제목 없음',
      subject: worksheetData.subject || '',
      grade: worksheetData.grade || '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      favorite: false,
      questionCount: worksheetData.questions ? worksheetData.questions.length : 0,
      thumbnailColor: worksheetData.thumbnailColor || '#4a6cf7'
    });
    
    // 학습지 목록 저장
    localStorage.setItem(ANONYMOUS_WORKSHEETS_KEY, JSON.stringify(worksheets));
    
    return { id: worksheetId, success: true };
  } catch (err) {
    console.error('익명 학습지 저장 오류:', err);
    return { success: false, error: err.message };
  }
};

/**
 * 익명 학습지 업데이트
 * @param {string} worksheetId 학습지 ID
 * @param {Object} worksheetData 수정할 학습지 데이터
 * @returns {Object} 업데이트 결과
 */
export const updateAnonymousWorksheet = (worksheetId, worksheetData) => {
  try {
    // 기존 학습지 데이터 가져오기
    const existingWorksheet = getAnonymousWorksheet(worksheetId);
    if (!existingWorksheet) {
      return { success: false, error: '학습지를 찾을 수 없습니다.' };
    }
    
    // 타임스탬프 업데이트
    const timestamp = new Date().toISOString();
    
    // 학습지 데이터 업데이트
    const updatedWorksheet = {
      ...existingWorksheet,
      ...worksheetData,
      id: worksheetId, // ID는 유지
      createdAt: existingWorksheet.createdAt, // 생성 시간 유지
      updatedAt: timestamp // 수정 시간 업데이트
    };
    
    // 개별 학습지 데이터 저장
    localStorage.setItem(
      `${ANONYMOUS_WORKSHEET_PREFIX}${worksheetId}`,
      JSON.stringify(updatedWorksheet)
    );
    
    // 학습지 목록 업데이트
    const worksheets = getAnonymousWorksheets();
    const updatedWorksheets = worksheets.map(ws => {
      if (ws.id === worksheetId) {
        return {
          ...ws,
          title: worksheetData.title || worksheetData.topic || ws.title,
          subject: worksheetData.subject || ws.subject,
          grade: worksheetData.grade || ws.grade,
          updatedAt: timestamp,
          questionCount: worksheetData.questions ? worksheetData.questions.length : ws.questionCount,
          thumbnailColor: worksheetData.thumbnailColor || ws.thumbnailColor
        };
      }
      return ws;
    });
    
    // 학습지 목록 저장
    localStorage.setItem(ANONYMOUS_WORKSHEETS_KEY, JSON.stringify(updatedWorksheets));
    
    return { id: worksheetId, success: true };
  } catch (err) {
    console.error('익명 학습지 업데이트 오류:', err);
    return { success: false, error: err.message };
  }
};

/**
 * 익명 학습지 삭제
 * @param {string} worksheetId 학습지 ID
 * @returns {Object} 삭제 결과
 */
export const deleteAnonymousWorksheet = (worksheetId) => {
  try {
    // 학습지 목록에서 제거
    const worksheets = getAnonymousWorksheets();
    const filteredWorksheets = worksheets.filter(ws => ws.id !== worksheetId);
    
    // 학습지 목록 저장
    localStorage.setItem(ANONYMOUS_WORKSHEETS_KEY, JSON.stringify(filteredWorksheets));
    
    // 개별 학습지 데이터 삭제
    localStorage.removeItem(`${ANONYMOUS_WORKSHEET_PREFIX}${worksheetId}`);
    
    // 즐겨찾기에서도 제거
    const favorites = getAnonymousFavorites();
    if (favorites.includes(worksheetId)) {
      const updatedFavorites = favorites.filter(id => id !== worksheetId);
      localStorage.setItem(ANONYMOUS_FAVORITES_KEY, JSON.stringify(updatedFavorites));
    }
    
    return { success: true };
  } catch (err) {
    console.error('익명 학습지 삭제 오류:', err);
    return { success: false, error: err.message };
  }
};

/**
 * 익명 학습지 즐겨찾기 목록 가져오기
 * @returns {Array} 즐겨찾기된 학습지 ID 목록
 */
export const getAnonymousFavorites = () => {
  const storedFavorites = localStorage.getItem(ANONYMOUS_FAVORITES_KEY);
  if (storedFavorites) {
    try {
      return JSON.parse(storedFavorites);
    } catch (err) {
      console.error('익명 즐겨찾기 데이터 파싱 오류:', err);
      return [];
    }
  }
  return [];
};

/**
 * 익명 학습지 즐겨찾기 토글
 * @param {string} worksheetId 학습지 ID
 * @returns {Object} 토글 결과
 */
export const toggleAnonymousFavorite = (worksheetId) => {
  try {
    // 즐겨찾기 목록 가져오기
    const favorites = getAnonymousFavorites();
    
    // 즐겨찾기 상태 확인
    const isFavorite = favorites.includes(worksheetId);
    
    // 즐겨찾기 토글
    let updatedFavorites;
    if (isFavorite) {
      // 즐겨찾기 해제
      updatedFavorites = favorites.filter(id => id !== worksheetId);
    } else {
      // 즐겨찾기 추가
      updatedFavorites = [...favorites, worksheetId];
    }
    
    // 즐겨찾기 목록 저장
    localStorage.setItem(ANONYMOUS_FAVORITES_KEY, JSON.stringify(updatedFavorites));
    
    // 학습지 목록에서 즐겨찾기 상태 업데이트
    const worksheets = getAnonymousWorksheets();
    const updatedWorksheets = worksheets.map(ws => {
      if (ws.id === worksheetId) {
        return { ...ws, favorite: !isFavorite };
      }
      return ws;
    });
    
    // 학습지 목록 저장
    localStorage.setItem(ANONYMOUS_WORKSHEETS_KEY, JSON.stringify(updatedWorksheets));
    
    // 개별 학습지 데이터 업데이트
    const worksheet = getAnonymousWorksheet(worksheetId);
    if (worksheet) {
      worksheet.favorite = !isFavorite;
      localStorage.setItem(
        `${ANONYMOUS_WORKSHEET_PREFIX}${worksheetId}`,
        JSON.stringify(worksheet)
      );
    }
    
    return { success: true, isFavorite: !isFavorite };
  } catch (err) {
    console.error('익명 즐겨찾기 토글 오류:', err);
    return { success: false, error: err.message };
  }
};

/**
 * 익명 학습지 데이터 존재 여부 확인
 * @returns {boolean} 익명 사용자 학습지 데이터가 있으면 true, 없으면 false
 */
export const hasAnonymousWorksheetData = () => {
  const worksheets = getAnonymousWorksheets();
  return worksheets.length > 0;
};

/**
 * 익명 학습지 데이터 모두 삭제
 */
export const clearAnonymousWorksheetData = () => {
  try {
    // 모든 학습지 목록 가져오기
    const worksheets = getAnonymousWorksheets();
    
    // 각 학습지 데이터 삭제
    worksheets.forEach(worksheet => {
      localStorage.removeItem(`${ANONYMOUS_WORKSHEET_PREFIX}${worksheet.id}`);
    });
    
    // 학습지 목록 삭제
    localStorage.removeItem(ANONYMOUS_WORKSHEETS_KEY);
    
    // 즐겨찾기 목록 삭제
    localStorage.removeItem(ANONYMOUS_FAVORITES_KEY);
    
    return { success: true };
  } catch (err) {
    console.error('익명 학습지 데이터 삭제 오류:', err);
    return { success: false, error: err.message };
  }
}; 